import {
    addQuarters,
    addWeeks,
    endOfQuarter,
    endOfWeek,
    getQuarter,
    getYear,
    startOfQuarter,
    startOfWeek,
} from 'date-fns';
import {
    formatMonthLabel,
    formatShortDate,
    getCurrentMonth,
    monthToDateRange,
    shiftMonth,
} from '../../../utils/dateUtils';

export type PeriodUnit = 'week' | 'month' | 'quarter';

export interface PeriodState {
    unit: PeriodUnit;
    offset: number;
}

export const DEFAULT_PERIOD: PeriodState = { unit: 'month', offset: 0 };

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

const clampToNow = (date: Date, now: Date): Date => (date > now ? now : date);

export const periodToDateRange = ({
    unit,
    offset,
}: PeriodState): { start: Date; end: Date } => {
    const now = new Date();

    if (unit === 'month') {
        const monthString = shiftMonth(getCurrentMonth(), -offset);
        const range = monthToDateRange(monthString);
        return {
            start: new Date(`${range.start}T00:00:00`),
            end: clampToNow(new Date(`${range.end}T23:59:59.999`), now),
        };
    }

    if (unit === 'week') {
        const anchor = addWeeks(now, -offset);
        return {
            start: startOfWeek(anchor, WEEK_OPTIONS),
            end: clampToNow(endOfWeek(anchor, WEEK_OPTIONS), now),
        };
    }

    const anchor = addQuarters(now, -offset);
    return {
        start: startOfQuarter(anchor),
        end: clampToNow(endOfQuarter(anchor), now),
    };
};

export const periodToLabel = ({ unit, offset }: PeriodState): string => {
    if (unit === 'month') {
        return formatMonthLabel(shiftMonth(getCurrentMonth(), -offset));
    }

    if (unit === 'week') {
        const anchor = addWeeks(new Date(), -offset);
        const start = startOfWeek(anchor, WEEK_OPTIONS);
        const end = endOfWeek(anchor, WEEK_OPTIONS);
        return `${formatShortDate(start)} – ${formatShortDate(end)}`;
    }

    const anchor = addQuarters(new Date(), -offset);
    return `Q${getQuarter(anchor)} ${getYear(anchor)}`;
};

export const isCurrentPeriod = (period: PeriodState): boolean =>
    period.offset === 0;

export const shiftPeriod = (
    period: PeriodState,
    delta: number
): PeriodState => ({
    ...period,
    offset: Math.max(0, period.offset - delta),
});

export const withPeriodUnit = (unit: PeriodUnit): PeriodState => ({
    unit,
    offset: 0,
});
